import api from './api';

class AssetMasterDataService {
  // Get all asset-related master data for dropdowns
  static async getAssetDropdowns() {
    try {
      const response = await api.get('/master-data/dropdowns');
      return response.data;
    } catch (error) {
      console.error('Error fetching asset dropdown data:', error);
      throw this.handleError(error);
    }
  }

  // Get asset types from master data
  static async getAssetTypes() {
    try {
      const response = await api.get('/master-data/AssetType');
      // API returns { success: true, data: [...] }
      // Axios response.data is the JSON body, so response.data.data is the array
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      // Fallback if structure is different
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching asset types:', error);
      throw this.handleError(error);
    }
  }

  // Get asset subtypes based on selected type
  static async getAssetSubtypes(assetType) {
    try {
      const response = await api.get(`/master-data/AssetSubtype?parentValue=${encodeURIComponent(assetType)}`);
      // API returns { success: true, data: [...] }
      // Axios response.data is the JSON body, so response.data.data is the array
      let data = [];
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        data = response.data.data;
      } else if (Array.isArray(response.data)) {
        data = response.data;
      }
      return { data };
    } catch (error) {
      console.error('Error fetching asset subtypes:', error);
      throw this.handleError(error);
    }
  }

  // Get departments from master data
  static async getDepartments() {
    try {
      const response = await api.get('/master-data/Department');
      // API returns { success: true, data: [...] }
      // Axios response.data is the JSON body, so response.data.data is the array
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      // Fallback if structure is different
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching departments:', error);
      throw this.handleError(error);
    }
  }

  // Get depreciation methods from master data
  static async getDepreciationMethods() {
    try {
      const response = await api.get('/master-data/DepreciationMethod');
      // API returns { success: true, data: [...] }
      // Axios response.data is the JSON body, so response.data.data is the array
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      // Fallback if structure is different
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching depreciation methods:', error);
      throw this.handleError(error);
    }
  }

  // Get asset statuses from master data
  static async getAssetStatuses() {
    try {
      const response = await api.get('/master-data/AssetStatus');
      // API returns { success: true, data: [...] }
      // Axios response.data is the JSON body, so response.data.data is the array
      if (response.data && response.data.success && Array.isArray(response.data.data)) {
        return response.data.data;
      }
      // Fallback if structure is different
      return Array.isArray(response.data) ? response.data : [];
    } catch (error) {
      console.error('Error fetching asset statuses:', error);
      throw this.handleError(error);
    }
  }

  // Get all asset-related master data in one call
  static async getAllAssetMasterData() {
    try {
      const [typesResponse, departmentsResponse, depreciationMethodsResponse, statusesResponse] = await Promise.all([
        this.getAssetTypes(),
        this.getDepartments(),
        this.getDepreciationMethods(),
        this.getAssetStatuses()
      ]);

      // Responses are already arrays (extracted in individual methods)
      return {
        types: Array.isArray(typesResponse) ? typesResponse : [],
        departments: Array.isArray(departmentsResponse) ? departmentsResponse : [],
        depreciationMethods: Array.isArray(depreciationMethodsResponse) ? depreciationMethodsResponse : [],
        statuses: Array.isArray(statusesResponse) ? statusesResponse : []
      };
    } catch (error) {
      console.error('Error fetching all asset master data:', error);
      throw this.handleError(error);
    }
  }

  // Error handler
  static handleError(error) {
    if (error.response) {
      // Server responded with error status
      return {
        message: error.response.data?.message || 'An error occurred',
        status: error.response.status,
        data: error.response.data
      };
    } else if (error.request) {
      // Request was made but no response received
      return {
        message: 'Network error - please check your connection',
        status: 0,
        data: null
      };
    } else {
      // Something else happened
      return {
        message: error.message || 'An unexpected error occurred',
        status: 0,
        data: null
      };
    }
  }
}

export default AssetMasterDataService;
